/**
 * BigInt-safe conversion between atomic (raw) units and human-readable
 * decimal strings.
 *
 * Why this exists: every place in the Swap screen used to do
 *
 *     Number(quote.expected_amount_out) / Math.pow(10, decimal)
 *
 * which silently loses precision once the raw value crosses
 * Number.MAX_SAFE_INTEGER (2^53 ≈ 9e15). For ETH (18 decimals) that's
 * anything ≥ 0.01 ETH. The displayed number is "close enough" but
 * comparing equal-looking strings, doing min-received math, or — in
 * the worst case — forgetting the divisor altogether produces
 * wei-shaped numbers in the UI.
 *
 * This module centralises the conversion so every display site uses
 * the same hardened path. We accept atomic as `string | bigint`
 * (LeoKit returns raw wei as a JSON number or string; we stringify on
 * receipt). All arithmetic is done in BigInt and we only call Number
 * at the very end of a `formatAtomic` call for display purposes —
 * after the value has already been scaled down to human units.
 */

/**
 * Convert an atomic value + decimal count into a human-readable
 * fixed-point string. Trims trailing zeros and the dangling decimal
 * point.
 *
 * @param atomic raw value in the asset's smallest unit. Accepts a
 *               number (must be integer) or string (preferred for big
 *               values from JSON).
 * @param decimal exponent (`8` for ZEC, `18` for ETH, `6` for USDC).
 * @param displayDecimals how many fractional digits to keep in the
 *                 OUTPUT string. Default 6 — plenty for most assets,
 *                 won't make ETH look like dust noise.
 */
export function atomicToHuman(
  atomic: string | number | bigint,
  decimal: number,
  displayDecimals = 6,
): string {
  if (atomic === null || atomic === undefined) return "0";
  // Coerce non-integer numeric inputs by stringifying — protects
  // against `0.0001 * 1e18` style floats arriving here. If the string
  // already has a decimal point we treat it as the literal value.
  const s = typeof atomic === "string"
    ? atomic
    : typeof atomic === "bigint"
      ? atomic.toString()
      : String(atomic);
  // Strip a leading "+" but keep a possible negative sign.
  const cleaned = s.replace(/^\+/, "").trim();
  if (!cleaned || cleaned === "0") return "0";
  // Reject anything that already looks like a float — at our boundary
  // we should only receive integer raw atomic units. If a caller
  // accidentally passes "0.5" expecting it to mean half an atomic
  // unit, that's a bug and we'd rather throw than silently mis-scale.
  if (cleaned.includes(".") || cleaned.includes("e") || cleaned.includes("E")) {
    throw new Error(
      `atomicToHuman expected an integer atomic value, got "${s}". ` +
        `Pass the raw on-chain value as a string/bigint, not a float.`,
    );
  }
  let bi: bigint;
  try {
    bi = BigInt(cleaned);
  } catch {
    return "0";
  }
  const neg = bi < 0n;
  const abs = neg ? -bi : bi;
  const dec = Math.max(0, Math.floor(decimal));
  const base = 10n ** BigInt(dec);
  const whole = abs / base;
  const frac = abs % base;
  // Pad fractional with leading zeros to its full decimal width, then
  // truncate to displayDecimals + strip trailing zeros.
  const fracFull = frac.toString().padStart(dec, "0");
  const fracTrunc = fracFull.slice(0, Math.max(0, displayDecimals));
  const trimmed = fracTrunc.replace(/0+$/, "");
  const wholeStr = whole.toString();
  const sign = neg ? "-" : "";
  if (trimmed.length === 0) return `${sign}${wholeStr}`;
  return `${sign}${wholeStr}.${trimmed}`;
}

/**
 * Same as `atomicToHuman` but returns a Number. Use this only when
 * the result will be multiplied/divided further (e.g. computing
 * min-received from a slippage %). Precision is lost above
 * MAX_SAFE_INTEGER — for display, prefer `atomicToHuman`.
 */
export function atomicToNumber(
  atomic: string | number | bigint,
  decimal: number,
): number {
  const human = atomicToHuman(atomic, decimal, /* maxDigits */ 18);
  const n = Number(human);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Read `out_asset_decimal` from a LeoKit-shaped quote raw, with a
 * narrow per-chain fallback table. The previous fallback of `8` was
 * actively dangerous — it makes ETH (18) and USDC (6) both display
 * wrong by orders of magnitude when the field is missing. Better:
 * derive from the asset id's ticker so the wrong default never leaks.
 *
 * @param raw the quote's `raw` payload
 * @param assetId the LeoKit asset id we're displaying (e.g. "ETH.ETH").
 *                Used for the fallback lookup only.
 */
export function decimalsForOutputAsset(
  raw: { out_asset_decimal?: unknown } | null | undefined,
  assetId: string,
): number {
  if (raw && typeof (raw as { out_asset_decimal?: unknown }).out_asset_decimal === "number") {
    return (raw as { out_asset_decimal: number }).out_asset_decimal;
  }
  // Per-ticker fallback table. Keys are upper-cased tickers extracted
  // from "CHAIN.TICKER-…" ids. The fallback for an unknown ticker is
  // 18 (most ERC-20s + native EVM gas tokens) — strictly safer than
  // 8 because under-dividing wei never overflows the UI; under-
  // dividing micro-ZEC just shows extra zeros.
  const after = assetId.split(".")[1] ?? assetId;
  const ticker = (after.split("-")[0] ?? after).toUpperCase();
  switch (ticker) {
    case "ZEC":
    case "BTC":
    case "DOGE":
    case "LTC":
      return 8;
    case "USDC":
    case "USDT":
      return 6;
    case "ETH":
    case "POL":
    case "MATIC":
    case "BNB":
    case "AVAX":
    default:
      return 18;
  }
}
